import type { ReactNode } from 'react';
import { beginWhopLogin, logoutOfWhopSession, type AuthGateState } from '../../lib/auth';

interface AuthGateScreenProps {
  authGateState: AuthGateState;
  authRedirectError: string | null;
  onRetry: () => void;
}

export function AuthGateScreen({ authGateState, authRedirectError, onRetry }: AuthGateScreenProps) {
  if (authGateState.status === 'loading') {
    return (
      <AccessGateShell>
        <AccessGateCard
          eyebrow="Whop Session"
          title="Checking your MeridianView access"
          description="We are verifying your current Whop session and product entitlement before loading live market data."
        >
          <StatusLine label="Status" value="Connecting to MeridianView API" />
        </AccessGateCard>
      </AccessGateShell>
    );
  }

  if (authGateState.status === 'error' || authGateState.data == null) {
    return (
      <AccessGateShell>
        <AccessGateCard
          eyebrow="Auth Error"
          title="Whop authentication is not ready"
          description={authGateState.error ?? 'The app could not verify your session state.'}
        >
          <div className="flex flex-wrap items-center gap-3">
            <GateButton onClick={onRetry}>Retry</GateButton>
          </div>
        </AccessGateCard>
      </AccessGateShell>
    );
  }

  if (!authGateState.data.authenticated) {
    return (
      <AccessGateShell>
        <AccessGateCard
          eyebrow="Whop Sign-In"
          title="Sign in to MeridianView AI"
          description="All heatmap data is now gated behind Whop authentication and a valid product entitlement."
        >
          {authRedirectError != null && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 font-sans text-[12px] text-red-200">
              {authRedirectError}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <GateButton onClick={beginWhopLogin}>Sign In With Whop</GateButton>
            <GateSecondaryButton onClick={onRetry}>Refresh</GateSecondaryButton>
          </div>
        </AccessGateCard>
      </AccessGateShell>
    );
  }

  return (
    <AccessGateShell>
      <AccessGateCard
        eyebrow="Access Required"
        title="Your Whop account is signed in, but this product is not active"
        description="Use the hosted Whop checkout link, then refresh this page once the membership is active."
      >
        <div className="grid gap-2 font-mono text-[11px] text-neutral-400">
          <StatusLine
            label="Whop User"
            value={authGateState.data.user.email ?? authGateState.data.user.whopUserId}
          />
          <StatusLine label="Access Level" value={authGateState.data.accessLevel} />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {authGateState.data.checkoutUrl != null && authGateState.data.checkoutUrl !== '' ? (
            <GateLinkButton href={authGateState.data.checkoutUrl}>Buy Access</GateLinkButton>
          ) : (
            <span className="font-mono text-[11px] text-neutral-500">
              Checkout link is not configured yet.
            </span>
          )}
          <GateSecondaryButton onClick={onRetry}>Refresh Access</GateSecondaryButton>
          <GateSecondaryButton onClick={logoutOfWhopSession}>Sign Out</GateSecondaryButton>
        </div>
      </AccessGateCard>
    </AccessGateShell>
  );
}

function AccessGateShell({ children }: { children: ReactNode }) {
  return (
    <div className="dark flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top,rgba(59,130,246,0.18),transparent_32%),linear-gradient(180deg,#0b0b0b_0%,#060606_100%)] px-4 py-8">
      <div className="w-full max-w-3xl">{children}</div>
    </div>
  );
}

function AccessGateCard({
  eyebrow,
  title,
  description,
  children,
}: {
  eyebrow: string;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-white/[.08] bg-black/55 shadow-[0_20px_80px_rgba(0,0,0,0.45)] backdrop-blur-md">
      <div className="border-b border-white/[.06] px-6 py-5">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-blue-300/80">
          {eyebrow}
        </span>
        <h1 className="mt-3 font-sans text-2xl font-semibold tracking-tight text-neutral-100">
          {title}
        </h1>
        <p className="mt-2 max-w-2xl font-sans text-[14px] leading-6 text-neutral-400">
          {description}
        </p>
      </div>

      <div className="grid gap-5 px-6 py-6">{children}</div>
    </div>
  );
}

function StatusLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-neutral-500">{label}</span>
      <span className="text-neutral-200">{value}</span>
    </div>
  );
}

function GateButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-10 items-center justify-center rounded-md border border-blue-400/30 bg-blue-500/15 px-4 font-mono text-[11px] font-semibold tracking-[0.08em] text-blue-100 transition-all duration-150 hover:bg-blue-500/25"
    >
      {children}
    </button>
  );
}

function GateSecondaryButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-10 items-center justify-center rounded-md border border-white/[.08] bg-white/[.04] px-4 font-mono text-[11px] font-semibold tracking-[0.08em] text-neutral-300 transition-all duration-150 hover:bg-white/[.08] hover:text-neutral-100"
    >
      {children}
    </button>
  );
}

function GateLinkButton({ children, href }: { children: ReactNode; href: string }) {
  return (
    <a
      href={href}
      className="inline-flex h-10 items-center justify-center rounded-md border border-green-400/25 bg-green-500/12 px-4 font-mono text-[11px] font-semibold tracking-[0.08em] text-green-100 transition-all duration-150 hover:bg-green-500/20"
    >
      {children}
    </a>
  );
}
